import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';

interface SchemaOption {
  id: string;
  name: string;
  schema_name: string;
}

interface LoginFormProps {
  onSwitchToRegister?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onSwitchToRegister }) => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [selectedSchema, setSelectedSchema] = useState('');
  const [schemas, setSchemas] = useState<SchemaOption[]>([]);
  const [schemasLoaded, setSchemasLoaded] = useState(false);
  const [schemasLoading, setSchemasLoading] = useState(false);
  const [showSchemaSelect, setShowSchemaSelect] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginStep, setLoginStep] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Fetch schemas after verifying email + password
  const lookupSchemas = async (email: string, password: string) => {
    if (!email || !/\S+@\S+\.\S+/.test(email) || !password) return;
    setSchemasLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.SCHEMAS_LOOKUP}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      // A 5xx may not carry a JSON body — parse defensively so the real
      // failure surfaces instead of being masked as "no databases".
      const raw = await response.text();
      let data: { success?: boolean; message?: string; data?: unknown } = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch {
        data = { message: raw?.slice(0, 200) || `Server error (${response.status})` };
      }
      if (response.ok && data.success && Array.isArray(data.data)) {
        setSchemas(data.data as SchemaOption[]);
      } else {
        setSchemas([]);
        setErrors(prev => ({
          ...prev,
          general: data.message || `Could not load databases (${response.status})`,
        }));
      }
    } catch (error) {
      setSchemas([]);
      setErrors(prev => ({
        ...prev,
        general: error instanceof Error ? error.message : 'Could not reach the server',
      }));
    } finally {
      setSchemasLoaded(true);
      setSchemasLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    if (name === 'email' || name === 'password') {
      setSchemas([]);
      setSchemasLoaded(false);
      setSelectedSchema('');
      if (showSchemaSelect) setShowSchemaSelect(false);
    }
  };

  const handleToggleSchema = () => {
    if (!showSchemaSelect) {
      const newErrors: { [key: string]: string } = {};
      if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Enter a valid email first';
      }
      if (!formData.password || formData.password.length < 6) {
        newErrors.password = 'Enter your password first';
      }
      if (Object.keys(newErrors).length > 0) {
        setErrors(prev => ({ ...prev, ...newErrors }));
        return;
      }
      lookupSchemas(formData.email, formData.password);
    } else {
      setSelectedSchema('');
      setSchemas([]);
      setSchemasLoaded(false);
    }
    setShowSchemaSelect(!showSchemaSelect);
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    if (showSchemaSelect && schemas.length > 0 && !selectedSchema) {
      newErrors.schema = 'Please select a database';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setIsSubmitting(true);
      setLoginStep('Encrypting & verifying credentials...');
      // Simulate step progression while login API call runs
      const stepTimer1 = setTimeout(() => setLoginStep('Authenticating with Supabase Auth...'), 800);
      const stepTimer2 = setTimeout(() => setLoginStep('Validating password hash (bcrypt)...'), 1600);
      const stepTimer3 = setTimeout(() => setLoginStep('Looking up user role & schema...'), 2400);
      const stepTimer4 = setTimeout(() => setLoginStep('Fetching user permissions & modules...'), 3200);
      const stepTimer5 = setTimeout(() => setLoginStep('Preparing your dashboard...'), 4000);
      try {
        await login(formData.email, formData.password, selectedSchema || undefined);
      } finally {
        [stepTimer1, stepTimer2, stepTimer3, stepTimer4, stepTimer5].forEach(clearTimeout);
      }
    } catch (error) {
      setLoginStep('');
      setErrors({
        general: error instanceof Error ? error.message : 'Login failed. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
      setLoginStep('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8fbff] to-[#eaf2ff] flex flex-col lg:flex-row">
      {/* Left Panel - Welcome Section */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-16 lg:px-20 py-16 lg:py-0">
        <div className="max-w-lg mx-auto lg:mx-auto">
          <div className="flex items-center mb-8">
            <img src="/images/Consultare.png" alt="Consultare" className="h-12 w-auto object-contain" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-6 leading-tight">Welcome to Your Data Validator</h1>
          <p className="text-base text-gray-600 mb-10 leading-relaxed">
            Experience seamless productivity with our comprehensive SAP data validation platform designed for modern professionals and teams.
          </p>
          <div className="space-y-8">
            <div className="flex items-start group">
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mr-5 flex-shrink-0 group-hover:bg-blue-600 transition-colors duration-200">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Enterprise Security</h3>
                <p className="text-gray-600">Bank-level encryption and security protocols</p>
              </div>
            </div>
            <div className="flex items-start group">
              <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center mr-5 flex-shrink-0 group-hover:bg-orange-600 transition-colors duration-200">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Lightning Fast</h3>
                <p className="text-gray-600">Optimized performance for instant access</p>
              </div>
            </div>
            <div className="flex items-start group">
              <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mr-5 flex-shrink-0 group-hover:bg-green-600 transition-colors duration-200">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Team Collaboration</h3>
                <p className="text-gray-600">Work together seamlessly across all devices</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-16 lg:px-20 py-16 lg:py-0">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-10 border border-gray-100">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign In</h2>
              <p className="text-sm text-gray-600">Access your account and continue your journey</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {errors.general && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {errors.general}
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">EMAIL</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                    </svg>
                  </div>
                  <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                    className={`w-full pl-10 pr-3 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 ${errors.email ? 'border-red-300' : 'border-gray-300 hover:border-gray-400'}`}
                    placeholder="Enter your email" required />
                </div>
                {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">PASSWORD</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleInputChange}
                    className={`w-full pl-10 pr-10 py-3 border rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 ${errors.password ? 'border-red-300' : 'border-gray-300 hover:border-gray-400'}`}
                    placeholder="Enter your password" required />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-50 rounded-r-lg transition-colors duration-200">
                    <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                      {showPassword ? (
                        <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                      ) : (
                        <>
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
                {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
              </div>

              {/* Select Organization */}
              <div>
                <button type="button" onClick={handleToggleSchema}
                  className="flex items-center text-xs font-medium text-gray-500 hover:text-blue-600 transition-colors duration-200 mb-2">
                  <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm14 1a1 1 0 11-2 0 1 1 0 012 0zM2 13a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2zm14 1a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" />
                  </svg>
                  Select Organization
                  <svg className={`w-3.5 h-3.5 ml-1 transition-transform duration-200 ${showSchemaSelect ? 'rotate-180' : ''}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                {showSchemaSelect && (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">SELECT DATABASE</label>
                    {schemasLoading ? (
                      <div className="flex items-center justify-center py-3 border border-gray-300 rounded-lg bg-gray-50">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                        <span className="text-sm text-gray-500">Fetching available databases...</span>
                      </div>
                    ) : schemas.length > 0 ? (
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm14 1a1 1 0 11-2 0 1 1 0 012 0zM2 13a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2zm14 1a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <select value={selectedSchema}
                          onChange={(e) => { setSelectedSchema(e.target.value); if (errors.schema) setErrors(prev => ({ ...prev, schema: '' })); }}
                          className={`w-full pl-10 pr-8 py-3 border rounded-lg shadow-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-colors duration-200 appearance-none cursor-pointer ${errors.schema ? 'border-red-300' : 'border-gray-300'}`}>
                          <option value="">-- Select a database --</option>
                          {schemas.map((s) => (
                            <option key={s.id} value={s.schema_name}>{s.name}</option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    ) : schemasLoaded ? (
                      <div className="py-3 px-4 border border-gray-300 rounded-lg bg-gray-50 text-sm text-gray-500">
                        No databases found for this account
                      </div>
                    ) : null}
                    {errors.schema && <p className="mt-1 text-sm text-red-600">{errors.schema}</p>}
                  </div>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input id="remember-me" name="remember-me" type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700 cursor-pointer">Remember me</label>
                </div>
                <button type="button" className="text-sm text-blue-600 hover:text-blue-500 transition-colors duration-200"
                  onClick={(e) => { e.preventDefault(); }}>
                  Forgot password?
                </button>
              </div>

              {/* Sign In Button */}
              <button type="submit" disabled={isSubmitting}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center">
                {isSubmitting ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div><span className="transition-all duration-300">{loginStep || 'Signing in...'}</span></>
                ) : 'Sign In'}
              </button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-300" /></div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500 font-medium">Or continue with</span>
                </div>
              </div>

              {/* Google Sign-in */}
              <button type="button"
                className="w-full bg-white hover:bg-gray-50 text-gray-700 font-semibold py-4 px-6 rounded-xl border-2 border-gray-300 hover:border-gray-400 transition-all duration-200 flex items-center justify-center shadow-md hover:shadow-lg">
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google
              </button>

              {/* Register Link */}
              {onSwitchToRegister && (
                <div className="text-center mt-6">
                  <p className="text-sm text-gray-600">
                    Don't have an account?{' '}
                    <button type="button" onClick={onSwitchToRegister}
                      className="font-semibold text-blue-600 hover:text-blue-500 transition-colors duration-200 hover:underline">
                      Sign up here
                    </button>
                  </p>
                </div>
              )}
            </form>
          </div>

          <div className="text-center mt-8">
            <p className="text-sm text-gray-500 font-medium">&copy; 2024 Consultare. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
};
